import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getDemoDeal } from "../../data/portalDeals.js";

// JLL-pattern per-deal NDA gate. 2 steps:
//   1. Confirm role (Investor / Broker)
//   2. Click "Sign agreement" → DocuSign envelope opens in new tab
// On webhook completion, deal data room unlocks.

export default function PortalDealNda() {
  const { slug } = useParams();
  const deal = getDemoDeal(slug);
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState("investor");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!deal) {
    return (
      <div className="portal-page">
        <p>Deal not found.</p>
        <Link to="/portal/pipeline" className="portal-link">← Back to pipeline</Link>
      </div>
    );
  }

  async function continueStep1(e) {
    e.preventDefault();
    setStep(2);
  }

  async function signAgreement() {
    setError(null);
    setSubmitting(true);
    // Phase 3 wiring: POST /api/nda/start { deal_slug, role } → returns DocuSign signing URL
    // For now, simulate the call.
    try {
      const res = await fetch("/api/nda/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deal_slug: slug, role })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to start NDA (${res.status})`);
      }
      const { signing_url } = await res.json();
      // Open the DocuSign embedded signing in a new tab (JLL pattern with Adobe Sign)
      window.open(signing_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="portal-page">
      <div className="portal-breadcrumb">
        <Link to="/portal/pipeline" className="portal-link">Pipeline</Link> /{" "}
        <Link to={`/portal/pipeline/${slug}`} className="portal-link">{deal.name}</Link> /{" "}
        <span>CA/NDA</span>
      </div>

      <div className="nda-page">
        <div className="nda-page-left">
          <h1 className="portal-h1">{deal.name}</h1>
          <h2 className="portal-h2" style={{ marginTop: 4, color: "var(--portal-accent-strong)" }}>CA / NDA</h2>

          {step === 1 && (
            <form onSubmit={continueStep1}>
              <p className="spacer-s">
                To access the deal room, please confirm your role and click
                &ldquo;Continue&rdquo; to complete the Confidentiality Agreement (CA) /
                Non-Disclosure Agreement (NDA).
              </p>

              <h3 className="portal-h3">Role</h3>
              <div className="nda-role-row">
                <label className={"nda-role" + (role === "investor" ? " active" : "")}>
                  <input type="radio" name="role" value="investor"
                         checked={role === "investor"}
                         onChange={() => setRole("investor")} />
                  <span>Investor</span>
                </label>
                <label className={"nda-role" + (role === "broker" ? " active" : "")}>
                  <input type="radio" name="role" value="broker"
                         checked={role === "broker"}
                         onChange={() => setRole("broker")} />
                  <span>Broker / advisor</span>
                </label>
              </div>

              <div className="hero-actions spacer-m">
                <Link to={`/portal/pipeline/${slug}`} className="btn btn-ghost">View teaser</Link>
                <button type="submit" className="btn btn-primary">Continue →</button>
              </div>
            </form>
          )}

          {step === 2 && (
            <>
              <p className="spacer-s">Ready.</p>
              <p className="muted">
                Clicking &ldquo;Sign agreement&rdquo; opens the CA/NDA in a new tab via
                DocuSign. Once you sign, the deal&rsquo;s full memo, financials and data
                room unlock immediately. The signed PDF will be deposited in your Box folder.
              </p>
              {error && <p className="login-error">{error}</p>}
              <div className="hero-actions spacer-s">
                <button className="btn btn-ghost" onClick={() => setStep(1)} disabled={submitting}>
                  Back
                </button>
                <button className="btn btn-primary" onClick={signAgreement} disabled={submitting}>
                  {submitting ? "Preparing…" : "Sign agreement →"}
                </button>
              </div>
              <p className="muted small spacer-m">
                After signing, you&rsquo;ll be redirected back here. If the deal page
                doesn&rsquo;t unlock immediately, refresh — the DocuSign webhook may take
                10-30 seconds to fire.
              </p>
            </>
          )}
        </div>

        <div className="nda-page-right">
          <div className="nda-decoration">
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
              {Array.from({ length: 24 }).map((_, i) => (
                <ellipse key={i}
                         cx="100" cy="100" rx="80" ry="40"
                         fill="none"
                         stroke="rgba(212,177,106,0.18)"
                         strokeWidth="0.6"
                         transform={`rotate(${i * 7.5} 100 100)`} />
              ))}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
